<template>
    <div class="FieldsContainer">
        <Collapse
            v-for="(field, index) in fields"
            :key="index"
            :opened="checkOpen(field)"
            class="FieldCollapse"
        >
            <template #header>
                <div
                    class="CollapseHeader"
                    @click="toggleCollapse(field)"
                >
                    <div class="HeaderTitle">
                        <WText
                            :weight="checkOpen(field) ? 'semi' : 'medium'"
                            color="gray01"
                        >
                            {{ field.headerName }}
                        </WText>
                        <div
                            v-if="checkApplied(field)"
                            icon="filter"
                            color="primary"
                            :size="14"
                            :class="['AppliedMark', entityColor]"
                        />
                    </div>
                    <WIcon
                        :icon="checkOpen(field) ? 'chevron-up1' : 'chevron-down1'"
                        :color="checkOpen(field) ? 'black' : 'grayMedium'"
                        :size="20"
                    />
                </div>
            </template>
            <template #default>
                <div
                    v-if="checkRestorability(field)"
                    class="LogicalOperator"
                >
                    <WText
                        :size="12"
                        color="gray02"
                        class="LogicalOperatorLabel"
                    >
                        {{ $t('filters.match') }}
                    </WText>
                    <WSelect
                        v-model="logicalOperator"
                        name="logicalOperator"
                        :placeholder="$t('filters.field.match')"
                        :options="logicalOperatorsOptions"
                        @input="setValidity(field.field)"
                    />
                </div>
                <FieldFilter
                    v-for="(filter, filterIndex) in filters[field.field]"
                    :key="filterIndex"
                    :field="field"
                    :filter="filter"
                    :setOptions="getSetOptionsByField(field)"
                    :removeVisible="checkRestorability(field)"
                    @input="handleInput(field, filterIndex, $event)"
                    @remove="handleRemoveFilter(field, filterIndex)"
                />
                <div class="ActionContainer">
                    <div class="Left">
                        <WButton
                            v-if="!checkSet(field)"
                            outline
                            color="accent-blue"
                            iconLeft="plus"
                            :iconSize="16"
                            :disabled="!checkAggregability(field)"
                            @click="handleAddFilter(field)"
                        />
                    </div>
                    <div class="Right">
                        <WButton
                            outline
                            color="accent-blue"
                            iconLeft="trash-2"
                            :iconSize="16"
                            :disabled="!checkApplied(field)"
                            @click="handleCleanFilter(field)"
                        />
                        <WButton
                            color="accent-blue"
                            iconLeft="search"
                            :iconSize="16"
                            :disabled="!validity[field.field]"
                            @click="handleApplyFilter(field)"
                        />
                    </div>
                </div>
            </template>
        </Collapse>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { Collapse } from '@/components'
import FieldFilter from './FieldFilter.vue'
import {
    FILTER_INITIAL_VALUE, LOGICAL_OPERATORS, createModel, createFilterFromModel, getFilterTypeByFieldType,
} from '../constants'

export default {
    name: 'Fields',
    components: { Collapse, FieldFilter },
    props: {
        fields: VueTypes.array,
        filterModel: VueTypes.object,
        selectGroups: VueTypes.object,
        entityColor: VueTypes.string,
    },
    data () {
        this.logicalOperatorsOptions = LOGICAL_OPERATORS
        return {
            openField: '',
            filters: {},
            validity: {},
            logicalOperator: 'AND',
        }
    },
    watch: {
        filterModel: {
            deep: true,
            handler (newModel) {
                this.fields.forEach(({ field }) => {
                    this.setValidity(field, newModel)
                })
            },
        },
    },
    created () {
        this.initFilters()
    },
    methods: {
        initFilters () {
            this.fields.forEach(({ field }) => {
                const model = this.filterModel[field]
                const { conditions } = { ...model }
                const filters = conditions ? conditions.map(m => createFilterFromModel(m)) : [createFilterFromModel(model)]
                this.$set(this.filters, field, filters)
                this.setValidity(field)
            })
        },
        toggleCollapse (field) {
            this.openField = this.checkOpen(field) ? '' : field.field
        },
        checkOpen ({ field }) {
            return this.openField === field
        },
        checkApplied ({ field }) {
            return !!this.filterModel[field]
        },
        checkAggregability ({ field }) {
            return this.filters[field].every((filter) => {
                if (this.checkInRange(filter)) return !!filter.value && !!filter.valueTo
                return !!filter.value || !!filter.setValues.length
            })
        },
        checkRestorability ({ field }) {
            return this.filters[field].length > 1
        },
        checkSet ({ type }) {
            const filterType = getFilterTypeByFieldType(type)
            return filterType === 'set'
        },
        checkInRange ({ operator }) {
            return operator === 'inRange'
        },
        getModelByField ({ field }, filterIndex, altFilterModel) {
            const filterModel = altFilterModel || this.filterModel
            const model = filterModel[field]
            if (model?.conditions) return model.conditions[filterIndex]
            return model
        },
        handleAddFilter ({ field }) {
            this.filters[field].push(FILTER_INITIAL_VALUE)
            this.setValidity(field)
        },
        handleRemoveFilter ({ field }, filterIndex) {
            this.filters[field].splice(filterIndex, 1)
            this.setValidity(field)
        },
        handleCleanFilter ({ field }) {
            this.$emit('cleanFilter', field)
            this.$set(this.filters, field, [FILTER_INITIAL_VALUE])
        },
        handleApplyFilter ({ field, type }) {
            const model = createModel(this.filters[field], type, this.logicalOperator)
            this.$emit('applyFilter', field, model)
        },
        handleInput ({ field }, filterIndex, filter) {
            this.$set(this.filters[field], filterIndex, filter)
            this.setValidity(field)
        },
        setValidity (field, altFilterModel) {
            const filters = this.filters[field]
            const filterModel = altFilterModel || this.filterModel
            const value = filters.some((filter, filterIndex) => {
                const model = this.getModelByField({ field }, filterIndex, filterModel)
                const { conditions, logicalOperator } = filterModel[field] || {}
                const invalidConditons = filters.length !== conditions?.length
                const invalidLogicalOperator = this.logicalOperator !== logicalOperator
                if (this.compareModel(field, model, filter)) return (!!conditions && invalidConditons) || (!!logicalOperator && invalidLogicalOperator)
                if (this.checkInRange(filter)) return !!filter.value && !!filter.valueTo
                return !!filter.value || !!filter.setValues.length
            })
            this.$set(this.validity, field, value && this.checkAggregability({ field }) && !!this.logicalOperator)
        },
        compareModel (field, model, filter) {
            const sameValue = [model?.filter, model?.dateFrom].includes(filter.value)
            const sameValueTo = [model?.filterTo, model?.dateTo].includes(filter.valueTo)
            const sameSetValues = model?.values?.toString() === filter.setValues?.toString()
            const sameOperator = model?.type === filter.operator
            if (this.checkSet(field)) return sameSetValues
            if (this.checkInRange(filter)) return sameValue && sameValueTo && sameOperator
            return sameValue && sameOperator
        },
        getSetOptionsByField ({ selectGroup }) {
            const isFlag = selectGroup === 'COUNTRIES'
            const group = this.selectGroups[selectGroup]
            const options = Object.keys(group || {})
            return options.map(key => ({
                key: isFlag ? key : group[key],
                label: group[key],
            }))
        },
    },
}
</script>

<style lang="scss" scoped>
.FieldsContainer {
    padding-left: 14px;
    padding-right: 14px;
    padding-bottom: 18px;
    .FieldCollapse {
        padding-top: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid $gray-04;
        &:last-child {
            border-bottom: 0px;
        }
        .LogicalOperator {
            width: 100%;
            border-bottom: 1px solid $gray-05;
            padding: 8px 0 15px 0;
            margin-bottom: 5px;
            .LogicalOperatorLabel {
                margin-bottom: 2px;
            }
        }
        .CollapseHeader {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            cursor: pointer;
            .HeaderTitle {
                display: flex;
                flex-direction: row;
                align-items: center;
                .AppliedMark {
                    width: 10px;
                    height: 10px;
                    background-color: $danger;
                    border-radius: 100%;
                    margin-left: 4px;
                    margin-bottom: 2px;
                    flex-shrink: 0;
                    &.turquoise {
                        background-color: $turquoise;
                    }
                    &.cyan {
                        background-color: $cyan;
                    }
                    &.violet {
                        background-color: $violet;
                    }
                    &.pink {
                        background-color: $pink;
                    }
                }
            }
        }
        .ActionContainer {
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            margin-top: 14px;
            margin-bottom: 4px;
            gap: 8px;
            .Left, .Right {
                gap: 8px;
                display: flex;
                flex-direction: row;
            }
        }
    }
}

</style>
